import { useEffect, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";

// Handles cascading defaults for size/fit based on selected colour & size.
// Returns variant selections, derived option arrays, and activeSku.
export function useVariantCascade({
  item,
  control,
  fieldPrefix,
  watch,
  setValue,
}) {
  const selectedColour = useWatch({ name: `${fieldPrefix}.colour`, control });
  const selectedSize = useWatch({ name: `${fieldPrefix}.size`, control });
  const selectedFit = useWatch({ name: `${fieldPrefix}.fit`, control });

  const filterObjectArray = (arr, attribute, value) =>
    (arr || []).filter((o) => o[attribute] === value);
  const removeDuplicatesFromObjectArray = (arr, attribute) =>
    (arr || []).filter(
      (obj, index) =>
        (arr || []).findIndex((i) => i[attribute] === obj[attribute]) === index,
    );

  const sizes = useMemo(() => {
    if (!item.sizes || !selectedColour) return [];
    const filtered = filterObjectArray(item.sizes, "colour", selectedColour);
    return removeDuplicatesFromObjectArray(filtered, "code").sort(
      (a, b) => Number(a.sizeSeq) - Number(b.sizeSeq),
    );
  }, [item.sizes, selectedColour]);

  const fits = useMemo(() => {
    if (!item.fits || !selectedColour || !selectedSize) return [];
    const fitsColour = filterObjectArray(item.fits, "colour", selectedColour);
    return filterObjectArray(fitsColour, "size", selectedSize).sort(
      (a, b) => Number(a.fitsSeq) - Number(b.fitsSeq),
    );
  }, [item.fits, selectedColour, selectedSize]);

  const validSKU = () => {
    if (item.colours && !selectedColour) return false;
    if (item.sizes && !selectedSize) return false;
    if (item.fits && !selectedFit) return false;
    return true;
  };

  const currentSKU = useMemo(() => {
    if (!validSKU()) return null;
    return `${item.productCode}${selectedColour || ""}${selectedSize || ""}${
      selectedFit || ""
    }`;
  }, [item.productCode, selectedColour, selectedSize, selectedFit]);

  const [activeSku, setActiveSku] = useState(null);
  const lastSkuRef = useRef(null);
  useEffect(() => {
    if (currentSKU && currentSKU !== lastSkuRef.current) {
      lastSkuRef.current = currentSKU;
      setActiveSku(currentSKU);
    }
  }, [currentSKU]);

  // Cascade logic
  useEffect(() => {
    // Reset size/fit if colour removed
    if (!selectedColour) {
      if (selectedSize)
        setValue(`${fieldPrefix}.size`, "", { shouldDirty: false });
      if (selectedFit)
        setValue(`${fieldPrefix}.fit`, "", { shouldDirty: false });
      return;
    }
    // Validate / auto-set size
    if (item.sizes) {
      const sizeOptions = item.sizes.filter((s) => s.colour === selectedColour);
      const sizeValid =
        selectedSize && sizeOptions.some((s) => s.code === selectedSize);
      if (!sizeValid) {
        let candidate = "";
        if (sizeOptions.length === 1) candidate = sizeOptions[0].code;
        else {
          const flagged = sizeOptions.find(
            (s) => String(s.sizeDefault).toLowerCase() === "yes",
          );
          if (flagged) candidate = flagged.code;
        }
        if (candidate)
          setValue(`${fieldPrefix}.size`, candidate, { shouldDirty: false });
        else if (selectedSize)
          setValue(`${fieldPrefix}.size`, "", { shouldDirty: false });
        if (selectedFit)
          setValue(`${fieldPrefix}.fit`, "", { shouldDirty: false });
      }
    }
    // Validate / auto-set fit
    const effectiveSize = watch(`${fieldPrefix}.size`);
    if (item.fits && effectiveSize) {
      const fitOptions = item.fits.filter(
        (f) => f.colour === selectedColour && f.size === effectiveSize,
      );
      const fitValid =
        selectedFit && fitOptions.some((f) => f.code === selectedFit);
      if (!fitValid) {
        let candFit = "";
        if (fitOptions.length === 1) candFit = fitOptions[0].code;
        else {
          const flaggedFit = fitOptions.find(
            (f) => String(f.fitDefault).toLowerCase() === "yes",
          );
          if (flaggedFit) candFit = flaggedFit.code;
        }
        if (candFit)
          setValue(`${fieldPrefix}.fit`, candFit, { shouldDirty: false });
        else if (selectedFit)
          setValue(`${fieldPrefix}.fit`, "", { shouldDirty: false });
      }
    } else if (!effectiveSize && selectedFit) {
      setValue(`${fieldPrefix}.fit`, "", { shouldDirty: false });
    }
  }, [
    selectedColour,
    selectedSize,
    selectedFit,
    item.sizes,
    item.fits,
    fieldPrefix,
    setValue,
    watch,
  ]);

  return {
    selectedColour,
    selectedSize,
    selectedFit,
    sizes,
    fits,
    activeSku,
    validSKU,
  };
}
